import { useEffect, useState } from 'react';
import { Button, Container, Nav, Offcanvas } from 'react-bootstrap';
import { FaListUl } from 'react-icons/fa6';
import { Link, useLocation } from 'react-router-dom';
import './StickySubNav.css';

export interface SubNavItem {
    label: string;
    id?: string;
    url?: string;
}

interface StickySubNavProps {
    items: SubNavItem[];
    mobileTitle?: string;
}

export const StickySubNav = ({ items, mobileTitle = 'On This Page' }: StickySubNavProps) => {
    const location = useLocation();
    const [activeId, setActiveId] = useState<string | null>(null);
    const [showMobileNav, setShowMobileNav] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(80);
    const [subnavHeight, setSubnavHeight] = useState(48);

    useEffect(() => {
        const update = () => {
            const navbar = document.querySelector('.navbar') as HTMLElement;
            const primaryTabs = document.getElementById('why-grate-primary-tabs') as HTMLElement;
            const navH = navbar?.offsetHeight ?? 80;
            const tabsH = primaryTabs ? primaryTabs.offsetHeight : 0;
            setNavbarHeight(navH + tabsH);
            const subnav = document.querySelector('.sfc-subnav') as HTMLElement;
            if (subnav) setSubnavHeight(subnav.offsetHeight);
        };

        update();
        window.addEventListener('resize', update);

        const primaryTabs = document.getElementById('why-grate-primary-tabs');
        const ro = new ResizeObserver(update);
        if (primaryTabs) ro.observe(primaryTabs);

        const navbar = document.querySelector('.navbar');
        if (navbar) ro.observe(navbar);

        return () => {
            window.removeEventListener('resize', update);
            ro.disconnect();
        };
    }, []);

    const scrollOffset = navbarHeight + subnavHeight + 16;

    const handleAnchorClick = (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        setActiveId(id);
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: top - scrollOffset, behavior: 'smooth' });
        }
        setShowMobileNav(false);
    };

    const handleRouteClick = () => {
        setShowMobileNav(false);
    };

    const isActive = (item: SubNavItem) => {
        if (item.url) return location.pathname === item.url;
        return activeId === item.id;
    };

    const renderLink = (item: SubNavItem, className: string) => {
        if (item.url) {
            return (
                <Nav.Link
                    key={item.url}
                    as={Link}
                    to={item.url}
                    className={`${className}${isActive(item) ? ' active' : ''}`}
                    onClick={handleRouteClick}
                >
                    {item.label}
                </Nav.Link>
            );
        }
        return (
            <Nav.Link
                key={item.id}
                href={`#${item.id}`}
                className={`${className}${isActive(item) ? ' active' : ''}`}
                onClick={(e) => handleAnchorClick(item.id!, e)}
            >
                {item.label}
            </Nav.Link>
        );
    };

    return (
        <>
            <div className="sfc-subnav d-none d-md-block" style={{ top: navbarHeight }}>
                <Container>
                    <Nav className="sfc-subnav__pills">
                        {items.map((item) => renderLink(item, 'sfc-subnav__pill'))}
                    </Nav>
                </Container>
            </div>

            <div className="d-md-none sfc-subnav__mobile-btn">
                <Button
                    className="rounded-circle shadow sfc-subnav__mobile-trigger"
                    onClick={() => setShowMobileNav(true)}
                >
                    <FaListUl size={20} />
                </Button>
            </div>

            <Offcanvas show={showMobileNav} onHide={() => setShowMobileNav(false)} placement="start">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{mobileTitle}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Nav className="flex-column">
                        {items.map((item) => renderLink(item, 'sfc-subnav__offcanvas-link'))}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};